import React, { useState, useEffect, useRef } from 'react';

// Cada ação carrega uma instrução fixa (não depende do LLM interpretar "isso"/"melhor")
// e o dispatch anexa o texto exato da resposta selecionada + a pergunta que a gerou,
// para que o backend responda sobre o assunto certo, não sobre a Athena.
const ACTIONS = [
  { icon: '📖', label: 'Explicar Melhor', type: 'explain',
    instruction: 'Explique melhor a resposta selecionada, mantendo exatamente o mesmo assunto. Não fale sobre a Athena, sua missão, identidade ou sistema, a menos que a resposta selecionada seja sobre isso.' },
  { icon: '⚡', label: 'Resumir', type: 'summarize',
    instruction: 'Resuma a resposta selecionada em poucos pontos principais, mantendo os pontos essenciais e sem mudar de assunto.' },
  { icon: '🖼️', label: 'Gerar Imagem', type: 'image',
    instruction: 'Gere uma imagem que ilustre a entidade principal da resposta selecionada.' },
  { icon: '📋', label: 'Organizar', type: 'organize',
    instruction: 'Reorganize a resposta selecionada usando títulos, listas ou blocos, sem alterar o conteúdo nem mudar de assunto.' },
  { icon: '🎯', label: 'Em Passos', type: 'steps',
    instruction: 'Transforme a resposta selecionada em um passo a passo prático, somente se o conteúdo permitir, sem mudar de assunto.' },
];

// Janela de bloqueio após o clique — mesmo valor do guard de duplicidade em
// sendMessage (lastSentRef, 2500ms). Evita clique duplo/múltiplas ações
// simultâneas na mesma barra; a resposta em si continua chegando normalmente
// mesmo depois desse prazo (o dispatch já foi feito).
const BUSY_MS = 2500;

export function ResponseActions({ visible, responseText, userMessage, messageId }) {
  const [busyType, setBusyType] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  if (!visible) return null;

  function dispatch(action) {
    if (busyType) return; // evita clique duplicado enquanto uma ação está em voo
    setErrorType(null);
    setBusyType(action.type);
    try {
      window.dispatchEvent(new CustomEvent('athena-quick-action', {
        detail: {
          prompt: action.instruction,
          actionType: action.type,
          sourceMessageId: messageId,
          sourceResponseText: responseText || '',
          sourceUserMessage: userMessage || '',
        },
      }));
    } catch {
      setBusyType(null);
      setErrorType(action.type);
      return;
    }
    timerRef.current = setTimeout(() => setBusyType(null), BUSY_MS);
  }

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10,
      animation: 'fadeIn .2s ease',
    }}>
      {ACTIONS.map(a => {
        const isBusy = busyType === a.type;
        const disabled = !!busyType;
        return (
          <button
            key={a.label}
            onClick={() => dispatch(a)}
            disabled={disabled}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${errorType === a.type ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: 20, padding: '4px 10px',
              fontSize: 11, color: 'rgba(172,196,236,0.55)',
              cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
              transition: 'all .15s',
              whiteSpace: 'nowrap',
              opacity: disabled && !isBusy ? 0.45 : 1,
            }}
            onMouseEnter={e => {
              if (disabled) return;
              e.currentTarget.style.background    = 'rgba(255,255,255,0.07)';
              e.currentTarget.style.color         = 'rgba(172,196,236,0.9)';
              e.currentTarget.style.borderColor   = 'rgba(255,255,255,0.18)';
            }}
            onMouseLeave={e => {
              if (disabled) return;
              e.currentTarget.style.background    = 'rgba(255,255,255,0.03)';
              e.currentTarget.style.color         = 'rgba(172,196,236,0.55)';
              e.currentTarget.style.borderColor   = 'rgba(255,255,255,0.08)';
            }}
          >
            {isBusy ? '⏳' : a.icon} {isBusy ? 'Processando…' : a.label}
          </button>
        );
      })}
      {errorType && (
        <span style={{fontSize:10.5,color:'rgba(239,68,68,0.75)',alignSelf:'center'}}>
          Não consegui iniciar essa ação. Tenta de novo.
        </span>
      )}
    </div>
  );
}
